import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from './api';

export type DatabaseEnv = 'dev' | 'staging' | 'prod';

export interface EnvInfo {
  env: DatabaseEnv;
  label: string;
  available: boolean;
}

export type DeploymentMode = 'dev' | 'prod';

interface EnvironmentContextType {
  env: DatabaseEnv;
  setEnv: (env: DatabaseEnv) => void;
  availableEnvs: EnvInfo[];
  isLoading: boolean;
  /** True if only one environment is available (prod deployment) */
  isSingleEnvMode: boolean;
  /** The backend deployment mode (dev or prod) */
  deploymentMode: DeploymentMode;
}

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(undefined);

const STORAGE_KEY = 'qa-judge.selected-environment';

/**
 * Simplified EnvironmentProvider
 * 
 * The backend is now the single source of truth for environment resolution:
 * - In prod deployment: backend always uses 'prod' regardless of what frontend sends
 * - In dev deployment: backend uses the env param if valid, defaults to 'dev'
 * 
 * This means frontend cache mismatches can't cause crashes anymore.
 * We only use localStorage in multi-env mode for UX (remembering last selection).
 */
export function EnvironmentProvider({ children }: { children: ReactNode }) {
  const [env, setEnvState] = useState<DatabaseEnv>('prod');
  const [isLoading, setIsLoading] = useState(true);
  const [availableEnvs, setAvailableEnvs] = useState<EnvInfo[]>([
    { env: 'prod', label: 'Production', available: true },
  ]);
  const [deploymentMode, setDeploymentMode] = useState<DeploymentMode>('prod');

  // Fetch available environments on mount
  useEffect(() => {
    const initializeEnvironment = async () => {
      try {
        const data = await apiService.getEnvironments();
        
        // Set deployment mode and update document title
        const mode = data.deploymentMode || 'prod';
        setDeploymentMode(mode);
        document.title = mode === 'dev' ? 'Gieni Analytics Dev' : 'Gieni Analytics Prod';
        
        if (data.environments && data.environments.length > 0) {
          const envs = data.environments;
          setAvailableEnvs(envs);
          
          // Count available environments
          const availableCount = envs.filter((e) => e.available).length;
          const firstAvailable = envs.find((e) => e.available);
          
          if (availableCount === 1 && firstAvailable) {
            // Single-env mode (prod deployment): use the only available env
            // No need for localStorage - backend enforces this anyway
            setEnvState(firstAvailable.env);
            // Clear any stale cached value
            try { localStorage.removeItem(STORAGE_KEY); } catch {}
          } else if (availableCount > 1) {
            // Multi-env mode: try to restore cached preference
            let selectedEnv: DatabaseEnv = firstAvailable?.env || 'dev';
            try {
              const stored = localStorage.getItem(STORAGE_KEY);
              if (stored && ['dev', 'staging', 'prod'].includes(stored)) {
                const storedAvailable = envs.find((e) => e.env === stored && e.available);
                if (storedAvailable) {
                  selectedEnv = stored as DatabaseEnv;
                }
              }
            } catch {}
            setEnvState(selectedEnv);
          }
        }
      } catch (error) {
        console.error('[Environment] Failed to fetch environments:', error);
        // Keep 'prod' as default on error - backend will enforce correct env anyway
        document.title = 'Gieni Analytics Prod';
      } finally {
        setIsLoading(false);
      }
    };

    initializeEnvironment();
  }, []);

  // Check if we're in single-env mode
  const isSingleEnvMode = availableEnvs.filter((e) => e.available).length <= 1;

  const setEnv = (newEnv: DatabaseEnv) => {
    // In single-env mode, ignore changes (shouldn't happen, but defensive)
    if (isSingleEnvMode) return;
    
    // Only allow setting if env is available
    const envInfo = availableEnvs.find((e) => e.env === newEnv && e.available);
    if (!envInfo) {
      console.warn(`[Environment] Attempted to set unavailable env '${newEnv}'`);
      return;
    }
    
    setEnvState(newEnv);
    // Only persist in multi-env mode
    try {
      localStorage.setItem(STORAGE_KEY, newEnv);
    } catch {}
  };

  // Show loading state until environment is fetched
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <span className="text-sm text-muted-foreground">Initializing...</span>
        </div>
      </div>
    );
  }

  return (
    <EnvironmentContext.Provider value={{ env, setEnv, availableEnvs, isLoading, isSingleEnvMode, deploymentMode }}>
      {children}
    </EnvironmentContext.Provider>
  );
}

export function useEnvironment() {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
}
